import { MdDoNotDisturb } from 'react-icons/md';
import { PiFlask, PiUsers } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { EditIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  Image,
  Tag,
  Text,
} from '@chakra-ui/react';
import { useAppDispatch } from '../../redux/hooks';
import { HttpsProvider } from '../../redux/httpsProviders/types';
import { updateProviderDetails } from '../../redux/providerBuilder/providerDetails';
import { setSelectedInstances } from '../../redux/providerBuilder/selectedInstances';
import { setSelectedNetworkRequest } from '../../redux/providerBuilder/selectedNetworkRequest';
import { updateActiveStep } from '../../redux/providerBuilder/stepperDetails';
import { getProviderNetworkRequest } from '../../utils/provider-builder/register-provider-utils';
import { getFirebaseIdToken } from '../../utils/utils';

interface ProviderCardProps {
  providerDetail?: HttpsProvider | any;
  isSelected?: boolean;
  pointerCursor?: boolean;
  showEditButton?: boolean;
  onSelect?: (httpProviderId: string) => void;
  showDeleteButton?: boolean;
  handleProviderDelete?: (httpProviderId: string) => void;
  isAdminPanel?: boolean;
  removeActiveTag?: boolean;
  showTryDemoButton?: boolean;

  onOpenDemoModal?: () => void;
  httpProviderId?: string;
}
export const ProviderCardNew = ({
  providerDetail,
  showEditButton,
  isSelected = false,
  pointerCursor = true,
  onSelect,
  showDeleteButton = false,
  handleProviderDelete,
  isAdminPanel = false,
  removeActiveTag = false,
  showTryDemoButton = false,
  onOpenDemoModal,
}: ProviderCardProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleProviderEdit = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isAdminPanel) {
      return navigate(`/provider/${providerDetail?.httpProviderId}`);
    }

    const accessToken = (await getFirebaseIdToken()) ?? '';
    dispatch(updateProviderDetails({ ...providerDetail }));
    dispatch(updateActiveStep(1));

    const data = await getProviderNetworkRequest(
      accessToken,
      providerDetail?.httpProviderId
    );

    if (!data) {
      return navigate(`/provider/${providerDetail?.httpProviderId}`);
      // if (isUserAdmin) {
      // 	return navigate(`/provider/${providerDetail?.httpProviderId}`)
      // }

      // return toast({
      // 	title: 'Not supported',
      // 	description: 'Please create a new provider',
      // 	status: 'error',
      // 	duration: 5000,
      // 	isClosable: true,
      // 	position: 'top-right',
      // })
    }

    const networkData = data?.requestData;

    if (networkData?.isCascading) {
      if (networkData?.selectedInstances?.length) {
        const instances = [...networkData.selectedInstances];
        dispatch(setSelectedInstances(instances));
        delete networkData['selectedInstances'];
      }
      dispatch(setSelectedNetworkRequest(networkData?.networkRequests));
      navigate('/provider-builder?isEdit=true');
    } else {
      if (networkData?.selectedInstances?.length) {
        const instances = networkData.selectedInstances.map((instance: any) => {
          return {
            ...instance,
            responseSelection: {
              ...instance.responseSelection,
              requestIndex: 0,
            },
          };
        });
        dispatch(setSelectedInstances(instances));
        delete networkData['selectedInstances'];
      }

      dispatch(setSelectedNetworkRequest([networkData]));
      navigate('/provider-builder?isEdit=true');
    }
  };

  const handleClick = () => {
    // check if onSelect is a function is passed in props
    if (onSelect && typeof onSelect === 'function') {
      onSelect(providerDetail.httpProviderId);
    }
  };

  const handleDeleteBtnClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (handleProviderDelete && typeof handleProviderDelete === 'function') {
      handleProviderDelete(providerDetail?.httpProviderId);
    }
  };

  return (
    <>
      <Card
        variant="filled"
        bgColor="#f7f9fb"
        borderRadius="12px"
        border={isSelected ? '1.5px solid #2EB67D' : '1.5px solid #f7f9fb'}
        cursor={pointerCursor ? 'pointer' : 'default'}
        maxW="280px"
        h="175px"
        _hover={pointerCursor ? { border: '1.5px solid #2EB67D' } : {}}
        onClick={handleClick}
      >
        <CardBody overflow="hidden" p={4}>
          <Flex direction="column" h="full" justifyContent="space-between">
            <Flex direction="column" gap={2.5}>
              <Flex alignItems="flex-start" justifyContent="space-between" gap={5}>
                <Flex flex={1} gap={2} direction="column" alignItems="flex-start">
                  <Flex alignItems="center" gap={2}>
                    {removeActiveTag ? null : providerDetail?.isActive ? (
                      <Tag
                        borderRadius={7}
                        bgColor="#daf3ec"
                        border="1.5px solid #ABEFC6"
                        color="#067647"
                        fontWeight={500}
                        size="xs"
                        px="5px"
                        py="1.5px"
                      >
                        <Text p="1.5px" className="xxx-small-font">
                          Active
                        </Text>
                      </Tag>
                    ) : (
                      <Tag
                        borderRadius={7}
                        bgColor="#FFFAEB"
                        border="1.5px solid #FEDF89"
                        color="#B54708"
                        fontWeight={500}
                        size="xs"
                        px="5px"
                        py="1.5px"
                      >
                        <Text p="1.5px" className="xxx-small-font">
                          Inactive
                        </Text>
                      </Tag>
                    )}
                    {providerDetail?.isVerified && (
                      <Tag
                        borderRadius={7}
                        bgColor="#daf3ec"
                        border="1.5px solid #ABEFC6"
                        color="#067647"
                        fontWeight={500}
                        size="xs"
                        px="5px"
                        py="1.5px"
                      >
                        <Text p="1.5px" className="xxx-small-font">
                          Verified
                        </Text>
                      </Tag>
                    )}
                    {providerDetail?.providerType === 'PRIVATE' && (
                      <Tag
                        borderRadius={7}
                        border="1.5px solid"
                        borderColor="blue.500"
                        color="blue.900"
                        colorScheme="blue"
                        fontWeight={500}
                        size="xs"
                        px="5px"
                        py="1.5px"
                      >
                        <Text className="xxx-small-font" p="1.5px" fontFamily="inter">
                          Private
                        </Text>
                      </Tag>
                    )}
                  </Flex>
                  <Heading
                    m={0}
                    className="x-small-font"
                    fontFamily="inter"
                    lineHeight={1.4}
                    wordBreak="break-word"
                    noOfLines={1}
                  >
                    {providerDetail?.name}
                  </Heading>
                </Flex>
                <Box flex={0.25}>
                  <Image
                    src={providerDetail?.logoUrl}
                    alt="NA"
                    objectFit="contain"
                    borderRadius="8px"
                    w="100%"
                  />
                </Box>
              </Flex>
              <Flex>
                <Text
                  className="xx-small-font"
                  color="gray"
                  fontFamily="inter"
                  wordBreak="break-word"
                  overflow="hidden"
                  lineHeight={1.2}
                  noOfLines={2}
                >
                  {providerDetail?.description
                    ? providerDetail.description
                    : `Proves ${providerDetail?.name}`}
                </Text>
              </Flex>
            </Flex>
            <Flex
              alignItems="center"
              justifyContent="space-between"
              mt={2}
              direction="row"
            >
              <Flex alignItems="center" gap={3}>
                <PiUsers color="gray" />
                <Text color="gray" className="xx-small-font">
                  {providerDetail?.usedInCount || 0}
                </Text>
              </Flex>
              <Flex gap={2}>
                {showTryDemoButton && (
                  <Button
                    as="button"
                    type="button"
                    size="sm"
                    borderWidth="0.1px"
                    borderColor="darkslategrey"
                    bg="grey.200"
                    _hover={{
                      bg: 'gray.200',
                    }}
                    aria-label="Try Now"
                    px={2}
                    onClick={(e) => {
                      onOpenDemoModal && onOpenDemoModal();
                      e.stopPropagation();
                    }}
                  >
                    <Flex alignItems="center" gap={2}>
                      <PiFlask size={12} color="gray" />
                      <Text
                        color="gray"
                        className="xxx-small-font"
                        fontFamily="inter"
                        letterSpacing="0.3px"
                      >
                        Try Now
                      </Text>
                    </Flex>
                  </Button>
                )}
                {showEditButton === true && (
                  <Button
                    size="xs"
                    variant="ghost"
                    ml="-2px"
                    colorScheme="blackAlpha"
                    onClick={handleProviderEdit}
                  >
                    <Flex alignItems="center" gap={2}>
                      <EditIcon boxSize={4} color="grey" />
                      <Text
                        color="gray"
                        mt="4px"
                        className="xxx-small-font"
                        fontFamily="inter"
                      >
                        Edit
                      </Text>
                    </Flex>
                  </Button>
                )}
                {showDeleteButton === true && providerDetail?.isActive && (
                  <Button
                    size="xs"
                    variant="ghost"
                    ml="-2px"
                    colorScheme="blackAlpha"
                    onClick={handleDeleteBtnClick}
                  >
                    <Flex alignItems="center" gap={2}>
                      <MdDoNotDisturb
                        size={17}
                        style={{ marginTop: '1px', marginRight: '-1px' }}
                        color="grey"
                      />
                      <Text
                        color="gray"
                        mt="4px"
                        className="xxx-small-font"
                        fontFamily="inter"
                      >
                        Deactivate
                      </Text>
                    </Flex>
                  </Button>
                )}
              </Flex>
            </Flex>
          </Flex>
        </CardBody>
      </Card>
    </>
  );
};
